import { Action, ButtonKind, DetailFieldProps, DetailPanel, FieldType, FieldValue, ModalId, Tab, UIPermission, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useDataTypesHooks } from "../../../../../features/data-types"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { Expense, Ticket } from "../../../state/types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Labels } from "../../../../../features/data-types"
import { PaymentStatus } from "../../../../operations"
import { useEffect, useMemo, useState } from "react"
import { UploadAdditionalTicketModal } from "../../ticket/upload-additional-ticket-modal/uploadAdditionalTicketModal"
import { TicketList } from "../../ticket/ticket-list/ticketList"
import { DownloadTicketModal } from "../../ticket/download-ticket-modal/downloadTicketModal"
import { firstItem, toList } from "../../../../../utils/listUtils"
import { useTicketLoaders } from "../../../loaders/ticketLoaders"
import { UploadExpenseTicketModal } from "../../upload-expense-ticket-modal/uploadExpenseTicketModal"

export const ExpenseDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const expense = dataType! as Expense

  const [ticket, setTicket] = useState<Ticket>()
  const [currentTicket, setCurrentTicket] = useState<Ticket>()
  
  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_EXPENSES) || expense.archived
  const { formatDateTime } = useDataTypesHooks()

  const ticketLoaders = useTicketLoaders()

  const { openModal } = useUIActions()

  useEffect(() => {
    ticketLoaders().loadParentTickets(expense.id).then(loadedTickets => setTicket(firstItem(loadedTickets)))
  }, [])

  const title = `Gasto: ${expense.name}`
  const crumbs = useGetCrumbs('Gastos', RoutePath.EXPENSES, expense?.name)

  const fields: DetailFieldProps[] = [
    {
      name: 'code',
      type: FieldType.NUMBER,
      label: 'Código',
      render: (value?: FieldValue) => <>#{value}</>
    },
    { name: 'expenseTypeId', type: FieldType.TABLE, table: TableName.EXPENSE_TYPES, label: 'Tipo' },
    { name: 'date', type: FieldType.DATE, label: 'Fecha' },
    {
      name: 'branchId',
      type: FieldType.TABLE,
      table: TableName.BRANCHES,
      label: 'Sucursal'
    },
    {
      name: 'amount',
      type: FieldType.PRICE,
      label: 'Monto'
    },
    {
      name: 'paymentStatus',
      type: FieldType.TEXT,
      label: 'Estado de Pago',
      render: (value?: FieldValue) => <>{Labels.paymentStatusLabel(value! as PaymentStatus)}</>
    }
  ]
  if (expense.detail) {
    const detailField = {
      name: 'detail',
      type: FieldType.TEXT,
      label: 'Detalle'
    }
    fields.splice(4, 0, detailField)
  }
  if (expense.supplierId) {
    const supplierField = {
      name: 'supplierId',
      type: FieldType.TABLE,
      table: TableName.SUPPLIERS,
      label: 'Proveedor'
    }
    fields.splice(4, 0, supplierField)
  }
  if (expense.refundEmployeeId) {
    const refundEmployeeField = {
      name: 'refundEmployeeId',
      type: FieldType.TABLE,
      table: TableName.EMPLOYEES,
      label: 'Empleado'
    }
    fields.splice(4 + (expense.supplierId ? 1 : 0), 0, refundEmployeeField)
  }

  if (expense.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = expense.negativeStateAt ? ` - ${formatDateTime(new Date(expense.negativeStateAt))}` : ''
        return <><CompanyUserName userId={expense.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const onRemove = () => openModal(ModalId.REMOVE)

  const onUploadTicket = () => openModal(ModalId.UPLOAD_EXPENSE_TICKET)

  const actions: Action[] = useMemo(() => {
    const actionList: Action[] = []
    const isPaymentPending = expense.paymentStatus === PaymentStatus.PENDING
    const payedAmount = ticket?.payedAmount || 0
    const isEditable = isPaymentPending && payedAmount === 0

    if (!readOnly && isEditable) {
      actionList.push({ label: 'Eliminar', kind: 'secondary' as ButtonKind, icon: 'trash-fill', onClick: onRemove })
    }
    if (!ticket?.ticketUrl) {
      actionList.push({ label: 'Subir Factura', icon: 'upload', onClick: onUploadTicket })
    }

    return actionList
  }, [expense, ticket, readOnly])

  const tabs: Tab[] = []
  if (ticket) {
    tabs.push({
      id: 'tickets',
      title: 'Facturas / Comprobantes',
      content: <TicketList
        tickets={toList(ticket)}
        ticketLabel="Factura/Comp."
        readOnly
        onChangeCurrentTicket={setCurrentTicket}
      />
    })
  }

  return (<>
    <DetailPanel
      title={title}
      tableName={TableName.EXPENSES}
      crumbs={crumbs}
      fields={fields}
      data={expense}
      icon='receipt'
      tabs={tabs}
      readOnly={readOnly}
      actions={actions}
    />
    <DownloadTicketModal
      ticket={currentTicket}
      ticketLabel="Factura / Comprobante"
    />
    <UploadAdditionalTicketModal />
    <UploadExpenseTicketModal
      expense={expense}
      afterSubmit={setTicket}
    />
  </>)
}
